import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { getFormattedDate, getMoney } from 'utils/transform';
import { Label, Value } from './styles';
import { Else, If, Then } from 'react-if';
import api from 'services/api';
import { useAuth } from 'contexts/auth';
import { IAcordo, IParcelaAcordo } from 'interfaces/Acordo';
import { ICustomizacao } from 'interfaces/Customizacao';
import PopUpBoleto from 'dialogs/PopUpBoleto/PopUpBoleto';
import { PARCELA_EM_ABERTO } from 'utils/constants';
import CheckIcon from 'images/check-secondary.svg';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningPrimary from 'images/warning-primary.svg';

interface IDividaItemProps {
  item: IParcelaAcordo | null;
  acordo: IAcordo | null;
  custom: ICustomizacao | undefined;
}

function ParcelaItem({ item, acordo, custom }: IDividaItemProps) {
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery((theme: any) => theme?.breakpoints?.down('sm'));
  const { user } = useAuth();
  const [popUpOpen, setPopUpOpen] = React.useState(false);

  const handleClickOpen = () => {
    setPopUpOpen(true);
  };

  const onClickBaixarBoleto = async () => {
    try {
      setLoading(true);
      const { data: boleto } = await api.post('emitir-boleto-segunda-via', {
        Crm: acordo?.crm,
        Contrato: !!acordo?.contrato && acordo?.contrato[0]?.numero,
        CNPJ_CPF: user?.cnpJ_CPF,
        DataVencimento: item?.dataVencimento,
        CodigoCarteira: acordo?.codCarteira,
        Id: acordo?.idAcordo || item?.id,
        NossoNumero: item?.nossoNumero,
        QuantidadeParcela: item?.quantidadeParcelas,
        ValorBoleto: item?.valor,
        TipoBoleto: item?.tipoBoleto,
      });

      const response = await api.post(
        'download-boleto',
        {
          GuidBoleto: boleto.guidBoleto,
          UrlBoleto: boleto.urlBoleto,
          cnpj_cpf: user?.cnpJ_CPF,
          contrato: !!acordo?.contrato && acordo?.contrato[0]?.numero,
          crm: acordo?.crm,
          fileName: `Boleto_${!!acordo?.contrato &&
            acordo?.contrato[0]?.numero
          }_${new Date().getTime()}.pdf`,
          carteiraId: acordo?.codCarteira
        },
        {
          responseType: 'blob',
        },
      );

      const blob = new Blob([response.data], {
        type: 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url);

    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={0} style={{ backgroundColor: '#EFEFEF' }}>
      <Box p={2}>
        <Grid container spacing={2} alignItems="center">
          {!!item?.numeroParcela && (
          <Grid item xs={12} sm={3}>
            <Typography style={{ fontSize: 15, fontWeight: 400 }}>
              Pagamento{' '}
              {(Number(item?.numeroParcela) == 1) ? 'da Entrada' : item?.numeroParcela}
            </Typography>
          </Grid>
          )}
          {!!item?.dataVencimento && (
          <Grid item xs={6} sm={3}>
            <Label>Vencimento</Label>
            <Value regular noMargin>
              {getFormattedDate(item?.dataVencimento)}
            </Value>
          </Grid>
          )}
          {(!!item?.valorEntrada || !!item?.valor) && (
          <Grid item xs={6} sm={3}>
            <Label>Valor</Label>
            <Value regular noMargin>
              {getMoney(!!item?.valorEntrada ? item?.valorEntrada : item?.valor)}
            </Value>
          </Grid>
          )}
          <Grid item xs={12} sm={!!item?.boletoIndisponivel && !item?.permiteSegundaVia ? 3 : 12 } md={3}>
            <Box
              display="flex"
              justifyContent={isMobile ? 'flex-start' : 'flex-end'}
            >
              <If
                condition={!item?.boletoIndisponivel && !!item?.permiteSegundaVia}
              >
                <Then>
                  <Box
                    display="flex"
                    flexDirection="column"
                  >
                    <Typography
                      variant="caption"
                      style={{
                        textAlign: 'center',
                        marginTop: 5,
                        visibility: 'hidden',
                      }}
                    >
                      {item?.boletoComSenhaMsg}
                    </Typography>
                    <Grid item xs={12} sm={3} md={12}>
                      <Box style={{ display: "flex", justifyContent: isMobile ? "flex-start" : "flex-end", marginTop: isMobile ? "6px" : "0" }}>
                        <Button
                          fullWidth={isMobile}
                          style={{ textTransform: 'none', height: 44, minWidth: '90px'}}
                          variant="contained"
                          color="primary"
                          onClick={handleClickOpen}
                          size={isMobile ? 'small' : 'medium'}
                        >
                          Boleto
                        </Button>
                      </Box>
                    </Grid>
                  </Box>
                </Then>
                <Else>
                  <If
                    condition={
                      !!item?.boletoIndisponivel &&
                      !!(item?.status === PARCELA_EM_ABERTO)
                    }
                  >
                    <Then>
                      <Box style={{position: 'relative'}}>
                        <Chip
                          label="Boleto indisponível"
                          color="primary"
                          icon={<img src={WarningPrimary} alt="Warning" />}
                        />
                      </Box>
                    </Then>
                    <Else>
                      <If condition={item?.status !== PARCELA_EM_ABERTO}>
                        <Then>
                          <Box
                            display="flex"
                            justifyContent="center"
                            style={{
                              minWidth: isMobile ? 0 : 124,
                              padding: isMobile ? 0 : '0 16px',
                            }}
                          >
                            <img
                              style={{ marginRight: 10 }}
                              src={CheckIcon}
                              alt="Parcela paga"
                            />
                            <Typography variant="body2" color="textSecondary">
                              <strong>Pago</strong>
                            </Typography>
                          </Box>
                        </Then>
                        <Else>
                          {!!custom?.ParcelaBannerAlerta && (
                            <Box
                              display="flex"
                              justifyContent="center"
                              flexDirection="column"
                              style={{
                                minWidth: isMobile ? 0 : 124,
                                border: '1px solid #6C524B',
                                padding: 5,
                                borderRadius: 5,
                                background: "#F4DDD9",
                                color:"#6C524B"
                              }}
                            >
                              <Typography variant="body2">
                                <InfoOutlinedIcon style={{ width: 16, marginRight: 2, color: '#fe6f59'}}/>
                                {custom?.ParcelaBannerAlerta.split("|")[0]}
                              </Typography>
                              <Typography variant="body2" style={{ fontSize: 12 }}>
                                {custom?.ParcelaBannerAlerta.split("|")[1]}
                              </Typography>
                            </Box>
                          )}
                        </Else>
                      </If>
                    </Else>
                  </If>
                </Else>
              </If>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <PopUpBoleto 
        item={item} 
        acordo={acordo} 
        custom={custom} 
        popUpOpen={popUpOpen} 
        setPopUpOpen={setPopUpOpen}
      />
    </Paper>
  );
}

export default ParcelaItem;
